// src/components/NewBirthForm.jsx
import React, { useState } from "react";
import { useTheme } from '../context/ThemeContext';
import { parsePhoneNumberFromString, getCountries } from 'libphonenumber-js';
import axios from "axios";

const NewBirthForm = () => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0], // auto-pick today's date
    responseType: "",
    customTitle: '',
    title: "",
    firstName: "",
    surname: "",
    address: "",
    email: "",
    phoneMain: "",
    phoneAlt: "",
    gender: "",
    status: "",
    service_attended: "first_service",
    prayerRequest: "",
    country: "NG", // Default to Nigeria
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // ---------------- Helpers ----------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    // Format to international format for any selected country
    const phoneNumber = parsePhoneNumberFromString(phone, formData.country);
    return phoneNumber ? phoneNumber.number.replace('+', '') : phone.replace(/\D/g, "");
  };

  const isValidPhoneNumber = (phone, country) => {
    const phoneNumber = parsePhoneNumberFromString(phone, country);
    return phoneNumber ? phoneNumber.isValid() : false;
  };

  const handlePhoneBlur = (field) => (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    if (!formatted) return;

    if (isValidPhoneNumber(formatted, formData.country)) {
      setFormData((prev) => ({ ...prev, [field]: formatted }));
    }
  };

  // ---------------- Submit ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

        // Extra validation
  if (formData.title === 'Other' && !formData.customTitle.trim()) {
    alert('Please enter a custom title.');
    return;
  }

    try {
      if (!isValidPhoneNumber(formData.phoneMain)) {
        throw new Error("Please enter a valid Nigerian phone number for main phone");
      }

      const payload = {
        response_date: formData.date,
        decision:
          formData.responseType === "receive"
            ? "receive_jesus"
            : "rededicate",
        // title: formData.title,
        title: formData.title === 'Other' ? formData.customTitle : formData.title || null,
        first_name: formData.firstName,
        surname: formData.surname,
        contact_address: formData.address,
        email: formData.email,
        phone_main: formatPhoneNumber(formData.phoneMain),
        phone_alt: formData.phoneAlt
          ? formatPhoneNumber(formData.phoneAlt)
          : null,
        gender: formData.gender,
        residency_status: formData.status,
        service_attended: formData.service_attended,
        prayer_request: formData.prayerRequest || null,
      };

      const apiBase =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"
          ? process.env.REACT_APP_API_URL || "http://localhost:5000/api"
          : "/api";

      const res = await axios.post(`${apiBase}/new-birth-cards`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (!res?.data?.success) throw new Error("Failed to submit form");

      setMessage("✅ Form submitted successfully!");
      setFormData({
        date: "",
        responseType: "",
        title: "",
        firstName: "",
        surname: "",
        address: "",
        email: "",
        phoneMain: "",
        phoneAlt: "",
        gender: "",
        status: "",
        service_attended: "first_service",
        prayerRequest: "",
      });
    } catch (err) {
      setMessage(`❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- UI ----------------
  return (
    <form onSubmit={handleSubmit} className={`space-y-6 p-4 md:p-6 rounded-lg shadow ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
      <h2 className="text-2xl font-bold text-center">New Birth Form</h2>

      {/* Country selector for phone formatting */}
      <div>
        <label className="block font-medium mb-1">Country <span className="text-red-600">*</span></label>
        <select
          name="country"
          value={formData.country}
          onChange={handleChange}
          required
          className="w-full border rounded p-3"
        >
          {(() => {
            const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
            return getCountries().map((code) => (
              <option key={code} value={code}>
                {code === 'NG' ? `${displayNames.of(code)} (default)` : displayNames.of(code)}
              </option>
            ));
          })()}
        </select>
      </div>

      {/* Date */}
      <div>
        <label className="block font-medium mb-1">Date <span className="text-red-600">*</span></label>
        <input
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          required
          className="w-full border rounded p-3"
        />
      </div>

      {/* Invitation Response */}
      <div>
        <label className="block font-medium mb-1">
          I responded to the invitation to <span className="text-red-600">*</span>
        </label>
        <select
          name="responseType"
          value={formData.responseType}
          onChange={handleChange}
          required
          className="w-full border rounded p-3"
        >
          <option value="">Select</option>
          <option value="receive">
            Receive Jesus Christ as my Lord and Saviour
          </option>
          <option value="rededicate">Rededicate my life to Jesus Christ</option>
        </select>
      </div>

      {/* Service Attended */}
      <div>
        <label className="block font-medium mb-1">Service Attended <span className="text-red-600">*</span></label>
        <select
          name="service_attended"
          value={formData.service_attended}
          onChange={handleChange}
          required
          className="w-full border rounded p-3"
        >
          <option value="first_service">Sunday First Service</option>
          <option value="second_service">Sunday Second Service</option>
          <option value="second_service">Sunday Expression Church</option>
          <option value="midweek">Midweek Service</option>
        </select>
      </div>

      {/* Title */}
      <div>
        <label className="block font-medium mb-1">Title <span className="text-red-600">*</span></label>
        <select
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          className="w-full border rounded p-3"
        >
          <option value="">Select</option>
          <option value="Mr">Mr</option>
          <option value="Mrs">Mrs</option>
          <option value="Miss">Miss</option>
          <option value="Other">Others</option>
        </select>

        {formData.title === 'Other' && (
              <input
                type="text"
                placeholder="Enter title"
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={formData.customTitle}
                onChange={e => setFormData({ ...formData, customTitle: e.target.value })}
                required={formData.title === 'Other'}
              />
        )}
      </div>

      {/* First Name */}
      <div>
        <label className="block font-medium mb-1">First Name <span className="text-red-600">*</span></label>
        <input
          type="text"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          required
          className="w-full border rounded p-3"
        />
      </div>

      {/* Surname */}
      <div>
        <label className="block font-medium mb-1">Surname <span className="text-red-600">*</span></label>
        <input
          type="text"
          name="surname"
          value={formData.surname}
          onChange={handleChange}
          required
          className="w-full border rounded p-3"
        />
      </div>

      {/* Address */}
      <div>
        <label className="block font-medium mb-1">Contact Address <span className="text-red-600">*</span></label>
        <textarea
          name="address"
          value={formData.address}
          onChange={handleChange}
          required
          className="w-full border rounded p-3"
        />
      </div>

      {/* Email */}
      <div>
        <label className="block font-medium mb-1">Email Address <span className="text-red-600">*</span></label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full border rounded p-3"
        />
      </div>

      {/* Phone (Main) */}
      <div>
        <label className="block font-medium mb-1">Phone Number (Main) <span className="text-red-600">*</span></label>
        <input
          type="tel"
          name="phoneMain"
          value={formData.phoneMain}
          onChange={handleChange}
          onBlur={handlePhoneBlur("phoneMain")}
          required
          className="w-full border rounded p-3"
          placeholder={formData.country === 'NG' ? 'e.g. 08012345678' : 'Enter phone number'}
        />
      </div>

      {/* Phone (Alt) */}
      <div>
        <label className="block font-medium mb-1">Phone Number (Alternate)</label>
        <input
          type="tel"
          name="phoneAlt"
          value={formData.phoneAlt}
          onChange={handleChange}
          onBlur={handlePhoneBlur("phoneAlt")}
          className="w-full border rounded p-3"
          placeholder={formData.country === 'NG' ? 'e.g. 08012345678' : 'Enter phone number'}
        />
      </div>

      {/* Gender */}
      <div>
        <label className="block font-medium mb-1">Gender <span className="text-red-600">*</span></label>
        <select
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          required
          className="w-full border rounded p-3"
        >
          <option value="">Select</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
      </div>

      {/* Status */}
      <div>
        <label className="block font-medium mb-1">
          Are you Visiting, Resident, or Relocating to Abuja? <span className="text-red-600">*</span>
        </label>
        <select
          name="status"
          value={formData.status}
          onChange={handleChange}
          required
          className="w-full border rounded p-3"
        >
          <option value="">Select</option>
          <option value="visiting">Visiting</option>
          <option value="resident">Resident</option>
          <option value="relocating">Relocating</option>
        </select>
      </div>

      {/* Prayer Request */}
      <div>
        <label className="block font-medium mb-1">Prayer Request</label>
        <textarea
          name="prayerRequest"
          value={formData.prayerRequest}
          onChange={handleChange}
          className="w-full border rounded p-3"
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        // className="w-full md:w-auto px-6 py-3 bg-indigo-700 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        className="w-full py-3 rounded-xl font-semibold text-white disabled:opacity-50 bg-indigo-900 hover:bg-blue-700"
        
      >
        {loading ? "Submitting..." : "Submit"}
      </button>

      {message && <p className="mt-3">{message}</p>}
    </form>
  );
};

export default NewBirthForm;
